import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Heading,
  useToast,
  Spinner,
  Checkbox,
  Text,
  Textarea,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import ApiResponse from "../../../services/ApiResponse";
import ProtectedRoute from "../../../services/ProtectedRoute";
import { getDecodedToken } from "../../../services/Token";
import { Evento, EventoCreate } from "../../../services/models/Evento";
import { ApiValidationResponse } from "../../../services/models/ApiValidationResponse";
import handleApiError from "../../../utils/ApiErrorHandler";
import { EventoTipoDetails } from "../../../services/models/Tipo";
import { log } from "node:console";
import { formatParaInput } from "../../../utils/DateFormatter";
import { format } from "node:path";
import { Tipo } from "../../../services/models/Tipo";
import { InstitucionForEvento } from "../../../services/models/Institucion";
import { Horario } from "../../../services/models/Horario";

const EventoEditPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const decoded = getDecodedToken();

  // Estados
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [evento, setEvento] = useState<Evento | null>(null);
  const [tipos, setTipos] = useState<Tipo[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(true);
  const [instituciones, setInstituciones] = useState<InstitucionForEvento[]>([]);
  const [loadingInstituciones, setLoadingInstituciones] = useState(true);
  const [horarios, setHorarios] = useState<Horario>();
  const [usuarios, setUsuarios] = useState<{ id: number; nombre: string }[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(true);

  // Refs
  const tituloRef = useRef<HTMLInputElement>(null);
  const organizadorRef = useRef<HTMLSelectElement>(null);
  const institucionRef = useRef<HTMLSelectElement>(null);
  const ubicacionRef = useRef<HTMLInputElement>(null);
  const descripcionRef = useRef<HTMLTextAreaElement>(null);
  const inicioRef = useRef<HTMLInputElement>(null);
  const finRef = useRef<HTMLInputElement>(null);
  const esRecurrenteRef = useRef<HTMLInputElement>(null);
  const tipoRef = useRef<HTMLSelectElement>(null);
  const estadoRef = useRef<HTMLSelectElement>(null);

  const [selectedTipo, setSelectedTipo] = useState<number | "">("");
  const [sinTipoId, setSinTipoId] = useState<number>(0);
  const [selectedEstado, setSelectedEstado] = useState<number>(1);
  const [eventoTipoRelacion, setEventoTipoRelacion] = useState<EventoTipoDetails | null>(null);

  // Cargar evento
  useEffect(() => {
    const fetchEvento = async () => {
      try {
        const response = new ApiResponse<Evento>();
        await response.useFetch(`eventos/${id}`, "GET");
        if (response.data) {
          setEvento(response.data);
          setSelectedEstado(response.data.estado || 1);
        }
      } catch (error) {
        console.error("Error cargando evento:", error);
        toast({
          title: "Error",
          description: "No se pudo cargar el evento",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoadingData(false);
      }
    };

    if (id) {
      fetchEvento();
    }
  }, [id, toast]);

  // Cargar tipos
  useEffect(() => {
    const fetchTipos = async () => {
      try {
        const response = new ApiResponse<Tipo[]>();
        await response.useFetch("tipos", "GET");
        if (response.data) {
          const sinTipo = response.data.find(tipo => tipo.nombre.toLowerCase() === "sin tipo");
          sinTipo ? setSinTipoId(sinTipo?.id) : console.log("No se encontro el Sin Tipo");
          setTipos(response.data.filter(tipo => tipo.id !== sinTipo!.id));
        }
      } catch (error) {
        console.error("Error cargando tipos:", error);
      } finally {
        setLoadingTipos(false);
      }
    };

    fetchTipos();
  }, []);

  // Cargar instituciones
  useEffect(() => {
    const fetchInstituciones = async () => {
      try {
        const response = new ApiResponse<InstitucionForEvento[]>();
        await response.useFetch("instituciones", "GET");
        if (response.data) {
          setInstituciones(response.data);
        }
      } catch (error) {
        console.error("Error cargando instituciones:", error);
      } finally {
        setLoadingInstituciones(false);
      }
    };

    fetchInstituciones();
  }, [decoded?.rol_id]);

  // Cargar usuarios (para SuperUser)
  useEffect(() => {
    const fetchUsuarios = async () => {
      try {
        const response = new ApiResponse<{ id: number; nombre: string }[]>();
        await response.useFetch("usuarios", "GET");
        if (response.data) {
          setUsuarios(response.data);
        }
      } catch (error) {
        console.error("Error cargando usuarios:", error);
      } finally {
        setLoadingUsuarios(false);
      }
    };

    if (decoded?.rol_id === 1) {
      fetchUsuarios();
    } else {
      setLoadingUsuarios(false);
    }
  }, [decoded?.rol_id]);

  // Cargar horario
  useEffect(() => {
    const fetchHorarios = async () => {
      try {
        const response = new ApiResponse<Horario>();
        await response.useFetch(`horarios/${id}`, "GET");
        if (response.data) {
            const incioDate = formatParaInput(response.data.inicio);
            const finDate = formatParaInput(response.data.fin);
            response.data.inicio = incioDate;
            response.data.fin = finDate;
            setHorarios(response.data);
        }   
        } catch (error) {
        console.error("Error cargando horarios:", error);
      }
    };
    if (id) {
      fetchHorarios();
    }
    }, [id]);

  // Cargar evento-tipo (relación del evento con su tipo)
  useEffect(() => {
    const fetchEventoTipo = async () => {
      try {
        const response = new ApiResponse<EventoTipoDetails>();
        await response.useFetch(`evento-tipos/${id}`, "GET");
        if (response.data) {
          setEventoTipoRelacion(response.data);
          setSelectedTipo(response.data.tipo_id);
        }else {
            setSelectedTipo(sinTipoId);
        }
      } catch (error) {
        console.error("Error cargando evento-tipo:", error);
      }
    };

    if (id) {
      fetchEventoTipo();
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!tituloRef.current?.value) {
      toast({
        title: "Error de validación",
        description: "El título del evento es requerido",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (inicioRef.current?.value === "" || finRef.current?.value === "") {
      toast({
        title: "Error de validación",
        description: "Debes agregar al menos un horario con fecha de inicio y fin",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);

    try {
      // Preparar datos del evento para actualizar
      const eventoData: any = {
        titulo: tituloRef.current.value,
        descripcion: descripcionRef.current?.value || "",
        ubicacion: ubicacionRef.current?.value || undefined,
      };

      // SuperUser puede editar organizador e institución
      if (decoded?.rol_id === 1) {
        eventoData.organizador = organizadorRef.current?.value
          ? parseInt(organizadorRef.current.value)
          : evento?.organizador;
        eventoData.institucion_id = institucionRef.current?.value
          ? parseInt(institucionRef.current.value)
          : undefined;
        eventoData.estado = selectedEstado;
      }
      // Admin solo puede cambiar el estado
      else if (decoded?.rol_id === 2) {
        eventoData.estado = selectedEstado;
      }

      // Actualizar evento
      const eventoResponse = new ApiResponse<Evento>();
      await eventoResponse.useFetch(`eventos/${id}`, "PUT", eventoData);

      if (eventoResponse.responseCode !== 200 && eventoResponse.responseCode !== 204) {
        let errorMessage = "Error al actualizar evento";
        
        if (typeof eventoResponse.error === "object" && eventoResponse.error !== null) {
          if ("errors" in eventoResponse.error) {
            // Si es un objeto de validación
            handleApiError(eventoResponse.error as ApiValidationResponse, toast);
            setLoading(false);
            return;
          } else if ("message" in eventoResponse.error) {
            errorMessage = (eventoResponse.error as any).message;
          }
        } else if (typeof eventoResponse.error === "string") {
          errorMessage = eventoResponse.error;
        }
        
        throw new Error(errorMessage);
      }
      // Actualizar horario si cambió
      if (horarios && inicioRef.current && finRef.current) {
        if( horarios.inicio !== inicioRef.current.value || horarios.fin !== finRef.current.value) {
            const horarioData = {
            evento_id: parseInt(id!),
            inicio: inicioRef.current.value,
            fin: finRef.current.value,
            esRecurrente: esRecurrenteRef.current?.checked ?? false,
            };
            
            const horarioResponse = new ApiResponse();
            await horarioResponse.useFetch(`horarios/${horarios.id}`, "PUT", horarioData);
            
            if (horarioResponse.responseCode !== 200 && horarioResponse.responseCode !== 204) {
                if (
                    typeof horarioResponse.error === "object" &&
                    horarioResponse.error !== null &&
                    "errors" in horarioResponse.error
                ) {
                    handleApiError(horarioResponse.error as ApiValidationResponse, toast);
                    return;
                } else {
                    toast({
                    title: "Error al Actualizar el Horario.",
                    description:
                        horarioResponse.error ||
                        "Ocurrió un problema al actualizar el horario.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                    });
                }
            }
        }
      }

      // Actualizar tipo si cambió
      if (selectedTipo && eventoTipoRelacion) {
        const tipoActual = eventoTipoRelacion.tipo_id;
        
        if (selectedTipo !== tipoActual) {
          // Actualizar evento-tipo
          const tipoResponse = new ApiResponse();
          await tipoResponse.useFetch(
            `evento-tipos/${eventoTipoRelacion.evento_id}/${eventoTipoRelacion.tipo_id}`,
            "PUT",
            { tipo_id: selectedTipo }
          );

          if (tipoResponse.responseCode !== 200 && tipoResponse.responseCode !== 204) {
            console.warn("Advertencia: No se pudo actualizar el tipo de evento");
          }
        } 
      }
      // Si no hay tipo asignado y ahora se selecciona uno
      else if (selectedTipo && !eventoTipoRelacion) {
        const tipoResponse = new ApiResponse();
        await tipoResponse.useFetch("evento-tipos", "POST", {
          evento_id: parseInt(id!),
          tipo_id: selectedTipo,
        });

        if (tipoResponse.responseCode !== 201) {
          console.warn("Advertencia: No se pudo asignar el tipo de evento");
        }
      }
      else if(!selectedTipo && !eventoTipoRelacion)
      {
        const tipoResponse = new ApiResponse();
        await tipoResponse.useFetch("evento-tipos", "POST", {
          evento_id: parseInt(id!),
          tipo_id: sinTipoId,
        });

        if (tipoResponse.responseCode !== 201) {
          console.warn("Advertencia: No se pudo asignar el tipo de evento");
        }
      }

      toast({
        title: "Éxito",
        description: "Evento actualizado correctamente",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/dashboard");
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Error al actualizar el evento",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Validar permisos
  if (decoded?.rol_id !== 1 && decoded?.rol_id !== 2) {
    return (
      <Box p={5} bg="rgba(2, 54, 176, 0.9)" minH="100vh">
        <Heading color="white">No tienes permiso para acceder aquí</Heading>
      </Box>
    );
  }

  if (loadingData) {
    return (
      <Box p={5} bg="rgba(2, 54, 176, 0.9)" minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner color="white" size="xl" />
      </Box>
    );
  }

  if (!evento) {
    return (
      <Box p={5} bg="rgba(2, 54, 176, 0.9)" minH="100vh">
        <Heading color="white">Evento no encontrado</Heading>
      </Box>
    );
  }

  return (
    <ProtectedRoute roles={[1, 2]}>
      <Box p={5} bg="rgba(2, 54, 176, 0.9)" minH="100vh">
        <VStack spacing={6} maxW="1000px" mx="auto" align="stretch">
          <Heading color="white" textAlign="center">
            Editar Evento
          </Heading>

          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text color="black" fontWeight="bold">Campos no editables:</Text>
              <Text color="black" fontSize="sm">ID, Creado en, Likes, Dislikes, Participantes posibles</Text>
            </Box>
          </Alert>

          <Box bg="whiteAlpha.100" p={6} borderRadius="lg">
            <form onSubmit={handleSubmit}>
              <VStack spacing={4}>
                {/* Título */}
                <FormControl isRequired>
                  <FormLabel color="white">Título del Evento</FormLabel>
                  <Input
                    ref={tituloRef}
                    defaultValue={evento.titulo}
                    placeholder="Ej: Feria de Ciencias 2025"
                    bg="white"
                    color="black"
                  />
                </FormControl>

                {/* Descripción */}
                <FormControl>
                  <FormLabel color="white">Descripción</FormLabel>
                  <Textarea
                    name="descripcion"
                    ref={descripcionRef}
                    defaultValue={evento.descripcion || ""}
                    placeholder="Describe el evento en detalle..."
                    bg="white"
                    color="black"
                    rows={6}
                  />
                </FormControl>
                
                {/* Ubicación */}
                <FormControl>
                  <FormLabel color="white">Ubicación</FormLabel>
                  <Input
                    ref={ubicacionRef}
                    defaultValue={evento.ubicacion || ""}
                    placeholder="Ej: Auditorio Principal"
                    bg="white"
                    color="black"
                  />
                </FormControl>

                {/* Institución (solo para SuperUser) */}
                {decoded?.rol_id === 1 && (
                  <FormControl>
                    <FormLabel color="white">Institución</FormLabel>
                    {loadingInstituciones ? (
                      <Spinner color="white" />
                    ) : (
                      <Select ref={institucionRef} bg="white" color="black" defaultValue={evento.institucion_id || ""}>
                        <option value="">Sin institución</option>
                        {instituciones.map((institucion) => (
                          <option key={institucion.id} value={institucion.id}>
                            {institucion.nombre}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}

                {/* Organizador (solo para SuperUser) */}
                {decoded?.rol_id === 1 && (
                  <FormControl isRequired>
                    <FormLabel color="white">Organizador</FormLabel>
                    {loadingUsuarios ? (
                      <Spinner color="white" />
                    ) : (
                      <Select ref={organizadorRef} bg="white" color="black" defaultValue={evento.organizador}>
                        {usuarios.map((usuario) => (
                          <option key={usuario.id} value={usuario.id}>
                            {usuario.nombre}
                          </option>
                        ))}
                      </Select>
                    )}
                  </FormControl>
                )}

                {/* Horarios */}
                <FormControl isRequired>
                  <FormLabel color="white">Horarios</FormLabel>
                  <VStack spacing={3} align="stretch">
                      <Box
                        bg="whiteAlpha.200"
                        p={4}
                        borderRadius="md"
                        border="1px solid rgba(255,255,255,0.3)"
                      >
                        <HStack spacing={3} mb={3}>
                          <FormControl>
                            <FormLabel color="white" fontSize="sm">
                              Inicio
                            </FormLabel>
                            <Input
                              type="datetime-local"
                              ref={inicioRef}
                              defaultValue={horarios ? formatParaInput(horarios.inicio) : ""}
                              bg="white"
                              color="black"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel color="white" fontSize="sm">
                              Fin
                            </FormLabel>
                            <Input
                              type="datetime-local"
                              ref={finRef}
                              defaultValue={horarios ? formatParaInput(horarios.fin) : ""}
                              bg="white"
                              color="black"
                            />
                          </FormControl>
                        </HStack>

                        <FormControl display="flex" alignItems="center">
                          <Checkbox
                            defaultChecked={horarios?.esRecurrente || false}
                            ref={esRecurrenteRef}
                            colorScheme="blue"
                          />
                          <FormLabel color="white" fontSize="sm" mb={0} ml={2}>
                            Es recurrente
                          </FormLabel>
                        </FormControl>
                      </Box>
                  </VStack>
                </FormControl>

                {/* Tipos de eventos (editable) */}
                <FormControl isRequired>
                  <FormLabel color="white">Categoría del Evento</FormLabel>
                  {loadingTipos ? (
                    <Spinner color="white" />
                  ) : (
                    <Select
                      value={selectedTipo}
                      onChange={(e) => setSelectedTipo(e.target.value ? parseInt(e.target.value) : "")}
                      bg="white"
                      color="black"
                    >
                      <option value={sinTipoId}>Sin categoría</option>
                      {tipos.map((tipo) => (
                        <option key={tipo.id} value={tipo.id}>
                          {tipo.nombre}
                        </option>
                      ))}
                    </Select>
                  )}
                </FormControl>

                {/* Estado (editable para ambos roles) */}
                <FormControl isRequired>
                  <FormLabel color="white">Estado</FormLabel>
                  <Select
                    ref={estadoRef}
                    value={selectedEstado}
                    onChange={(e) => setSelectedEstado(parseInt(e.target.value))}
                    bg="white"
                    color="black"
                  >
                    <option value={1}>Activo</option>
                    <option value={0}>Inactivo</option>
                  </Select>
                </FormControl>

                {/* Información de referencia (no editables) */}
                <FormControl>
                  <FormLabel color="white">Creado en (No editable)</FormLabel>
                  <Input
                    disabled
                    value={new Date(evento.creado_en).toLocaleString("es-AR")}
                    bg="whiteAlpha.300"
                    color="white"
                  />
                </FormControl>

                {/* Botones */}
                <HStack spacing={4} pt={4}>
                  <Button
                    colorScheme="blue"
                    type="submit"
                    isLoading={loading}
                    width="50%"
                  >
                    Guardar Cambios
                  </Button>
                  <Button
                    colorScheme="gray"
                    onClick={() => navigate(-1)}
                    width="50%"
                  >
                    Cancelar
                  </Button>
                </HStack>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Box>
    </ProtectedRoute>
  );
};

export default EventoEditPage;